import { Component, Input, Optional, Self, HostBinding, Output, EventEmitter, HostListener } from '@angular/core';
import { Subject } from 'rxjs';
import { MatFormFieldControl } from '@angular/material/form-field';
import { CategoriesService } from '@service/categories.service';
import { Category } from '@model/category/category';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { CategoryType } from '@enum/category-type';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import $ from 'jquery';
import { UserService } from '@service/user/user.service';

@Component({
  selector: 'app-category-selection',
  templateUrl: './category-selection.component.html',
  styleUrls: ['./category-selection.component.scss'],

  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: CategorySelectionComponent,
    },
  ],
})
export class CategorySelectionComponent implements MatFormFieldControl<Category>, ControlValueAccessor {
  // this is made specificially for job where people should be allowed to select second level -> region but not first level -> country
  @Input()
  public onlySecondLevelSelectable: boolean = false;

  public id;

  public onContainerClick;

  // mat form field control stuff
  ///////////////////////////////////

  @HostBinding('attr.aria-describedby') describedBy = '';
  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  public onChange = (_: any) => {};
  public onTouched = (_: any) => {};
  writeValue(v: Category) {
    this.value = v;
  }
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  @Input()
  get value(): Category {
    return this._value;
  }
  set value(v: Category) {
    this._value = v;
    this.stateChanges.next();
    this.calcExpandedPanels();
  }
  public _value: Category;

  public placeholder;
  public focused;
  public empty;
  public shouldLabelFloat;

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  public errorState: boolean = false;

  @Output() valueChange: EventEmitter<Category>;

  public disabled: boolean = false;
  public stateChanges = new Subject<void>();

  change(v: Category) {
    this.onChange(this.value);
    this.onTouched(this.value);
  }

  ///////////////////////////////////

  public loading: boolean = false;

  @Input()
  public withReset: boolean = false;

  @Output()
  public onCategorySelect: Subject<any> = new Subject();

  public innerHeight: number;
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerHeight = window.innerHeight;
  }

  public categories: Category[] = null;

  @Input()
  set inputCategories(inputCategories: { type: CategoryType; selected?: number }) {
    if (inputCategories.type == null) {
      return;
    }

    this.categoriesService.getCategories(inputCategories.type).then((categories) => {
      // filter zimmerer cat
      if (inputCategories.type == CategoryType.blog) {
        if (!this.userService.user || !this.userService.user.ROLE_ZIMMERER) {
          categories = categories.filter((c) => c.name != 'Zimmerer-Treffpunkt');
        }
      }

      this.categories = categories;

      if (inputCategories.selected != null) {
        this.selectCategoryById_f(inputCategories.selected);
        this.calcExpandedPanels();
      }
    });
  }

  @Input()
  public isSideBar: boolean = true;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private categoriesService: CategoriesService,
    private userService: UserService,
  ) {
    this.valueChange = new EventEmitter();

    this.innerHeight = window.innerHeight;

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  // if one expands a panel -> scroll to it
  public scrollTo(classname) {
    // setTimeout(() => {
    //   let el = $(classname).first()
    //   // console.log('el.top',el.offset().top)
    //   // console.log('innerheight/4',(this.innerHeight/4))
    //   // console.log("scroll to",el)
    //   $([document.documentElement, document.body]).animate({
    //     scrollTop: el.offset().top - (this.innerHeight / 4)
    //   }, 50)
    // }, 200)
  }

  public reset() {
    this.value = null;
    this.valueChange.emit(null);
    this.stateChanges.next();
    this.onCategorySelect.next({ filter: 'marketplace_category_id', value: null });
    this.change(null);
  }

  public selectCategory(category: Category) {
    this.value = category;
    this.calcExpandedPanels();
    this.valueChange.emit(category);
    this.stateChanges.next();
    this.onCategorySelect.next({ filter: 'marketplace_category_id', value: category.id });
    this.change(category);
  }

  public categoriesEqual(x: Category, y: Category): boolean {
    if (
      typeof x != 'undefined' &&
      x != null &&
      typeof y != 'undefined' &&
      y != null &&
      typeof x['id'] != 'undefined' &&
      x['id'] != null &&
      typeof y['id'] != 'undefined' &&
      y['id'] != null
    )
      return x['id'] == y['id'];
    return false;
  }

  private calcExpandedPanels() {
    if (!this.value || !this.categories) {
      return;
    }

    this.collapseAllPanels();

    loop1: for (let c of this.categories) {
      loop2: for (let c1 of c.children) {
        if (c1.id == this.value.id) {
          c.childmatched = true;
          break loop1;
        }

        loop3: for (let c2 of c1.children) {
          if (c2.id == this.value.id) {
            c.childmatched = true;
            c1.childmatched = true;
            break loop1;
          }
        }
      }
    }
  }

  private collapseAllPanels() {
    if (!this.categories) {
      return;
    }

    for (let c of this.categories) {
      for (let c1 of c.children) {
        delete c1.childmatched;
      }
      delete c.childmatched;
    }
  }

  // this is needed if a category is given from outside as id
  // happens i.e. in marketplace category filter

  private selectCategoryById_f(id: number) {
    if (this.categories && id != null) {
      loop1: for (let c of this.categories) {
        if (c.id == id) {
          this.selectCategory(c);
          break loop1;
        }
        loop2: for (let c1 of c.children) {
          if (c1.id == id) {
            this.selectCategory(c1);
            break loop1;
          }
          loop3: for (let c2 of c1.children) {
            if (c2.id == id) {
              this.selectCategory(c2);
              break loop1;
            }
          }
        }
      }
    }
  }

  public get getIsSideBar() {
    return this.isSideBar;
  }
}
